/*
 * promo-popup.js
 * ---------------------------------------------------------------------------
 * Local replacement for two custom elements that used to be supplied by the
 * remote vendor script:
 *
 *   <promo-popup>  sections/promo-popup.liquid:2-156
 *   <modal-v2>     snippets/sizing-chart.liquid:1-93
 *
 * Both are vendor-specific: there is no Dawn upstream for either, so the
 * behaviour below is derived entirely from the Liquid markup contract.
 *
 * This is a CLASSIC (non-module) script sharing one global scope with
 * assets/secondary.js, so everything lives inside an IIFE and the two element
 * classes are named so they cannot collide with the 25 classes secondary.js
 * already declares at top level (CopyButton, PopupCopyButton, ModalDialog,
 * ModalOpener, CountdownTimer, MenuDrawer, SliderComponent, ...).
 *
 * Nothing here touches the newsletter form: sections/promo-popup.liquid:65 is a
 * real `{% form 'customer' %}` (native full-page POST to /contact). The success
 * popup that follows the POST is driven by the inline Liquid script at
 * sections/promo-popup.liquid:111-146 and by the already-implemented
 * PopupCopyButton in secondary.js. We must not duplicate or fight either.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------- *
   * Small local helpers (deliberately prefixed so they cannot collide with
   * anything in secondary.js, which is minified and has no exports).
   * ---------------------------------------------------------------------- */

  // Zero-pad to two characters, matching CountdownTimer.formatNumber in
  // assets/secondary.js: `formatNumber(t){return 1===t.toString().length?"0"+t:t}`
  function ppFormatNumber(value) {
    var str = String(value);
    return str.length === 1 ? '0' + str : str;
  }

  // localStorage is unavailable in some privacy modes and throws on access.
  // secondary.js contains zero storage usage, so there is no in-theme helper.
  function ppStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function ppStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  // event.target is normally an Element, but guard anyway: a click can land on
  // a non-Element node in edge cases and Element.prototype.closest would throw.
  function ppClosest(node, selector) {
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(selector);
  }

  // Wrap a listener so a throw inside popup code can never bubble out and
  // break unrelated behaviour (add-to-cart, variant selection, ...).
  function ppSafe(fn, context) {
    return function (event) {
      try {
        fn.call(context, event);
      } catch (error) {
        if (window.console && console.warn) {
          console.warn('[promo-popup.js]', error);
        }
      }
    };
  }

  /* ====================================================================== *
   * <promo-popup>
   * sections/promo-popup.liquid:2-8 (attributes), :9-155 (children)
   *
   * Rendered by layout/theme.liquid:660 as a STATIC section, so section.id is
   * literally "promo-popup". The whole body is behind
   * `{%- unless section.settings.mode == 'disabled' -%}` (:1), so when the
   * merchant disables the popup this element is never emitted at all.
   *
   * Open  = add `popup-modal--active` to `.sign-up-popup-modal`  (:10)
   *         + `popup-overlay--active` to `.sign-up-popup-overlay` (:9)
   * Close = remove those two classes.
   * Those are the only classes with matching CSS in assets/base.css
   * (`.popup-modal--active{transform:translate(-50%,-50%);visibility:visible;opacity:1}`
   *  and `.popup-overlay--active{width:100%;height:100%;background-color:rgba(0,0,0,.3)}`).
   * ====================================================================== */

  var PP_DISMISS_KEY = 'promo-popup-dismissed-' + window.location.host;
  // Written by the inline success script at sections/promo-popup.liquid:113/:121.
  var PP_SUBSCRIBED_KEY = 'promo-bar-subscribed-' + window.location.host;

  class PromoPopupElement extends HTMLElement {
    constructor() {
      super();
      this._ppInitialised = false;
      this._ppOpenTimeout = null;
      this._ppTimerInterval = null;
      this._ppSecondsLeft = 0;
    }

    connectedCallback() {
      // The theme editor re-renders the section, and the element could in
      // principle be moved; guard so we never double-bind or double-schedule.
      if (this._ppInitialised) return;
      this._ppInitialised = true;

      try {
        this.ppSetup();
      } catch (error) {
        if (window.console && console.warn) {
          console.warn('[promo-popup.js] setup failed', error);
        }
      }
    }

    disconnectedCallback() {
      this.ppClearTimers();
      if (this._ppEscHandler) {
        document.removeEventListener('keyup', this._ppEscHandler);
        this._ppEscHandler = null;
      }
      this._ppInitialised = false;
    }

    ppSetup() {
      // ---- children (all scoped to `this`; never document-wide, because the
      // success popup at :158-230 is a SIBLING and uses the same base classes)
      this.overlay = this.querySelector('.sign-up-popup-overlay'); // :9
      this.modal = this.querySelector('.sign-up-popup-modal'); // :10

      // If either is missing the section markup is not what we expect; bail
      // quietly rather than throwing.
      if (!this.overlay || !this.modal) return;

      // ---- attributes (sections/promo-popup.liquid:3-7)
      // data-test-mode is the literal "true" for BOTH mode=='testing' and
      // mode=='success_testing' (:3).
      this.testMode = this.dataset.testMode === 'true';
      // Schema :286-295 range 2..30 default 30.
      this.delayDays = parseInt(this.dataset.delayDays, 10);
      if (!isFinite(this.delayDays) || this.delayDays <= 0) this.delayDays = 30;
      // Schema :275-284 range 2..60 default 5.
      this.delaySeconds = parseInt(this.dataset.delaySeconds, 10);
      if (!isFinite(this.delaySeconds) || this.delaySeconds < 0) this.delaySeconds = 5;
      // Schema :303-312 range 1..10 step 0.5 default 3, unit "min" -> FLOAT MINUTES.
      this.timerMinutes = parseFloat(this.dataset.timerDuration);
      if (!isFinite(this.timerMinutes) || this.timerMinutes <= 0) this.timerMinutes = 3;

      // ---- countdown nodes (:54-61). The block only exists when
      // section.settings.display_timer is true (:53), so `data-display-timer`
      // (:6) is redundant; presence of the node is the real switch.
      this.timerEl = this.querySelector('.popup-modal__timer'); // :54
      this.timerMinutesEl = this.querySelector('.popup-modal__timer__minutes'); // :56
      this.timerSecondsEl = this.querySelector('.popup-modal__timer__seconds'); // :59
      // The "M", ":", "S" spans at :57/:58/:60 are static markup — never touched.

      // ---- close: one delegated listener covers all three close hooks —
      // the overlay (:9), the X button (:12) and the conditional dismiss
      // button (:151), all of which carry `promp-popup__close-btn`
      // (note the deliberate vendor typo: "promp", not "promo").
      this.addEventListener(
        'click',
        ppSafe(function (event) {
          var trigger = ppClosest(event.target, '.promp-popup__close-btn');
          if (!trigger) return;
          // The X (:12) and dismiss (:151) buttons carry no `type` attribute.
          // They sit outside the `{% form %}` (which spans :65-148) so they
          // cannot submit it, but preventDefault costs nothing and protects us
          // if the markup is ever nested differently.
          event.preventDefault();
          this.ppClose(true);
        }, this)
      );

      // Escape closes an open popup. Not determined by the Liquid, but it is
      // the conventional behaviour and changes no rendered markup.
      this._ppEscHandler = ppSafe(function (event) {
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        if (!this.ppIsOpen()) return;
        this.ppClose(true);
      }, this);
      document.addEventListener('keyup', this._ppEscHandler);

      // Seed the countdown digits so the two empty spans (:56, :59) are never
      // blank if the popup is already open (mode=='testing' pre-activates it).
      this.ppResetTimerDisplay();

      // ---- initial state -------------------------------------------------
      // In mode=='testing' Liquid has ALREADY put popup-modal--active /
      // popup-overlay--active on the markup (:9, :10). Be idempotent: never
      // remove them on init.
      if (this.ppIsOpen()) {
        this.ppStartTimer();
        return;
      }

      // mode=='success_testing' also sets data-test-mode="true" (:3) but does
      // NOT pre-activate this modal — it pre-activates the SUCCESS modal
      // (:158, :159). The element gets no attribute distinguishing the two
      // modes, so we detect the success preview from the DOM and stay out of
      // its way rather than stacking two popups.
      if (this.ppSuccessPopupVisible()) return;

      if (this.testMode) {
        // Schema info :272 — "Test mode displays your popup without delay each
        // time even after dismissing it." Bypass both the delay and the
        // frequency record.
        this.ppOpen();
        return;
      }

      if (this.ppShouldSuppress()) return;

      // FREQUENCY / DELAY: open `data-delay-seconds` seconds after the element
      // is connected (schema :278 — "Time interval, in seconds, after which the
      // modal will appear.").
      this._ppOpenTimeout = window.setTimeout(
        ppSafe(function () {
          this._ppOpenTimeout = null;
          // Re-check: the visitor may have subscribed in another tab, or the
          // success popup may have appeared in the meantime.
          if (this.ppSuccessPopupVisible() || this.ppShouldSuppress()) return;
          this.ppOpen();
        }, this),
        this.delaySeconds * 1000
      );
    }

    /* -- state ----------------------------------------------------------- */

    ppIsOpen() {
      return !!this.modal && this.modal.classList.contains('popup-modal--active');
    }

    // The success popup (:158-230) is a sibling of this element, not a
    // descendant, and it is owned by the inline Liquid script at :111-146.
    // We only ever READ its state.
    ppSuccessPopupVisible() {
      var successModal = document.querySelector('.success-popup-modal');
      return !!successModal && successModal.classList.contains('popup-modal--active');
    }

    // Suppress when the visitor dismissed the popup less than `delayDays` ago,
    // or when they already subscribed (the inline success script writes
    // PP_SUBSCRIBED_KEY at :121).
    ppShouldSuppress() {
      if (ppStorageGet(PP_SUBSCRIBED_KEY)) return true;

      var raw = ppStorageGet(PP_DISMISS_KEY);
      if (!raw) return false;

      var expires = 0;
      try {
        var parsed = JSON.parse(raw);
        expires = parsed && parsed.expires ? Number(parsed.expires) : 0;
      } catch (e) {
        // Not JSON — treat a bare timestamp as the expiry.
        expires = Number(raw) || 0;
      }
      if (!isFinite(expires) || expires <= 0) return false;
      return Date.now() < expires;
    }

    ppRecordDismissal() {
      var expires = Date.now() + this.delayDays * 24 * 60 * 60 * 1000;
      ppStorageSet(PP_DISMISS_KEY, JSON.stringify({ dismissed: true, expires: expires }));
    }

    /* -- open / close ---------------------------------------------------- */

    ppOpen() {
      if (!this.modal || !this.overlay) return;
      if (this.ppIsOpen()) return;

      this.modal.classList.add('popup-modal--active'); // base.css .popup-modal--active
      this.overlay.classList.add('popup-overlay--active'); // base.css .popup-overlay--active
      this.ppStartTimer();
    }

    // `record` is false for programmatic closes; true for a real user dismissal.
    ppClose(record) {
      if (this._ppOpenTimeout) {
        window.clearTimeout(this._ppOpenTimeout);
        this._ppOpenTimeout = null;
      }
      if (this.modal) this.modal.classList.remove('popup-modal--active');
      if (this.overlay) this.overlay.classList.remove('popup-overlay--active');
      this.ppStopTimer();

      // In test mode the frequency record is bypassed entirely (schema :272),
      // so do not write it — otherwise the merchant's next preview would be
      // suppressed once they switch the section back to "Enabled".
      if (record && !this.testMode) this.ppRecordDismissal();
    }

    /* -- countdown ------------------------------------------------------- */

    ppResetTimerDisplay() {
      if (!this.timerEl) return;
      this._ppSecondsLeft = Math.max(0, Math.round(this.timerMinutes * 60));
      this.ppPaintTimer();
    }

    ppPaintTimer() {
      var minutes = Math.floor(this._ppSecondsLeft / 60);
      var seconds = this._ppSecondsLeft % 60;
      // Write ONLY into the two spans Liquid leaves empty (:56, :59).
      if (this.timerMinutesEl) this.timerMinutesEl.textContent = ppFormatNumber(minutes);
      if (this.timerSecondsEl) this.timerSecondsEl.textContent = ppFormatNumber(seconds);
    }

    ppStartTimer() {
      if (!this.timerEl) return;
      if (this._ppTimerInterval) return; // already ticking

      // Restart from the full duration each time the popup opens.
      this.ppResetTimerDisplay();
      if (this._ppSecondsLeft <= 0) return;

      var self = this;
      this._ppTimerInterval = window.setInterval(function () {
        try {
          self._ppSecondsLeft -= 1;
          if (self._ppSecondsLeft <= 0) {
            self._ppSecondsLeft = 0;
            self.ppPaintTimer();
            self.ppStopTimer(); // stop at 00:00; leave the popup open
            return;
          }
          self.ppPaintTimer();
        } catch (error) {
          self.ppStopTimer();
        }
      }, 1000);
    }

    ppStopTimer() {
      if (this._ppTimerInterval) {
        window.clearInterval(this._ppTimerInterval);
        this._ppTimerInterval = null;
      }
    }

    ppClearTimers() {
      this.ppStopTimer();
      if (this._ppOpenTimeout) {
        window.clearTimeout(this._ppOpenTimeout);
        this._ppOpenTimeout = null;
      }
    }
  }

  if (!customElements.get('promo-popup')) {
    customElements.define('promo-popup', PromoPopupElement);
  }

  /* ====================================================================== *
   * <modal-v2>
   * snippets/sizing-chart.liquid:1-93
   *
   * NOTE: this snippet is currently DEAD CODE — nothing renders it. The sizing
   * chart actually shipped on the product page is the Dawn modal-opener /
   * modal-dialog pair at sections/main-product.liquid:851-878 and :1093-1175,
   * both already implemented in assets/secondary.js. This element is a
   * separate, parallel mechanism with its own
   * `.modal-v2__open-btn` / `.modal-v2__close-btn` / `data-open` contract and
   * must NOT be merged with ModalDialog/ModalOpener.
   *
   * There is also ZERO CSS for `.modal-v2__*` anywhere in the theme, so the
   * open/close state below has nothing to drive visually until CSS is authored.
   * ====================================================================== */

  class ModalV2Element extends HTMLElement {
    constructor() {
      super();
      this._mv2Initialised = false;
    }

    connectedCallback() {
      // The snippet's own inline script (snippets/sizing-chart.liquid:96-110)
      // moves this element into the variant picker's second `.form__label` on
      // DOMContentLoaded, which fires disconnectedCallback + a second
      // connectedCallback. Guard so listeners are bound exactly once — the same
      // pattern ModalDialog uses with `this.moved` in secondary.js.
      //
      // Unlike ModalDialog we must NOT relocate ourselves to document.body:
      // that inline script deliberately appends us into the label (:101).
      if (this._mv2Initialised) return;
      this._mv2Initialised = true;

      try {
        this.mv2Setup();
      } catch (error) {
        if (window.console && console.warn) {
          console.warn('[promo-popup.js] modal-v2 setup failed', error);
        }
      }
    }

    disconnectedCallback() {
      // Intentionally NOT resetting _mv2Initialised: the element is reparented
      // (not destroyed) by the inline script, and listeners bound with
      // addEventListener on the element itself survive a move.
      if (this._mv2Escape) {
        document.removeEventListener('keyup', this._mv2Escape);
        this._mv2Escape = null;
      }
    }

    mv2Setup() {
      this.panel = this.querySelector('.modal-v2__modal'); // :23
      this.overlay = this.querySelector('.modal-v2__overlay'); // :22

      // One delegated listener on the element itself (never an ancestor, so it
      // survives being reparented) handles both the open trigger (:8) and both
      // close triggers — the overlay (:22) and the borderless X (:24).
      this.addEventListener(
        'click',
        ppSafe(function (event) {
          if (ppClosest(event.target, '.modal-v2__close-btn')) {
            event.preventDefault();
            this.mv2Close();
            return;
          }
          if (ppClosest(event.target, '.modal-v2__open-btn')) {
            // The open button (:8) has no `type` attribute. After the inline
            // script reparents this element into a `<label>` inside the
            // variant picker, a bare click would both activate the label and
            // potentially submit an ancestor form, so suppress both.
            event.preventDefault();
            event.stopPropagation();
            this.mv2Open();
          }
        }, this)
      );

      this._mv2Escape = ppSafe(function (event) {
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        if (!this.mv2IsOpen()) return;
        this.mv2Close();
      }, this);
      document.addEventListener('keyup', this._mv2Escape);

      // Seed from `data-open` (:4 — `{{ block.settings.test_mode }}`, the
      // literal string "true"/"false"). Normalise so the attribute is always
      // present and always one of those two values.
      this.mv2Apply(this.dataset.open === 'true');
    }

    mv2IsOpen() {
      return this.dataset.open === 'true';
    }

    mv2Open() {
      this.mv2Apply(true);
    }

    mv2Close() {
      this.mv2Apply(false);
    }

    // `data-open` is the authoritative state channel because that is where the
    // Liquid seeds the initial state (:4). `modal-v2--active` is mirrored onto
    // the root purely as a CSS hook for whoever authors the missing styles —
    // no rule matches it today, so it is inert.
    mv2Apply(open) {
      this.dataset.open = open ? 'true' : 'false';
      this.classList.toggle('modal-v2--active', !!open);
    }
  }

  if (!customElements.get('modal-v2')) {
    customElements.define('modal-v2', ModalV2Element);
  }
})();
